import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

interface ContactCreateProps {
  onSubmit: () => void;
}

export const ContactCreate = ({ onSubmit }: ContactCreateProps) => {
  const api = useApi();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [type, setType] = useState<string>('');
  const [entityName, setEntityName] = useState<string>('');

  const { data } = useQuery(
    ['contacttypes'],
    api.contactTypesApiV1ContactTypesGet.bind(null, {}),
    { enabled: true },
  );

  const { data: entityNames } = useQuery(
    ['prepareEntityNames'],
    api.getEntityNamesApiV1GetEntityNamesGet.bind(null, {}),
    { enabled: true, cacheTime: 0, initialData: undefined },
  );

  useEffect(() => {
    if (data?.data.length) setType(data.data[0]);
  }, [data]);

  useEffect(() => {
    if (entityNames?.data.length) setEntityName(entityNames.data[0]);
  }, [entityNames]);

  const createContact = useMutation(api.createContactApiV1CreateContactPost, {
    onSuccess() {
      toast.success('Contact successfully created.');
      onSubmit();
    },
    onError: apiError,
  });

  const handleSubmit = () => {
    createContact.mutate({
      first_name: firstName,
      last_name: lastName,
      email: email,
      phone: phone,
      address: address,
      company_name: companyName,
      contact_type: type,
      entity_name: entityName,
    });
  }

  return (
    <Box pt={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="First Name"
            variant="outlined"
            sx={{ mb: 4 }}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Last Name"
            variant="outlined"
            sx={{ mb: 4 }}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </Grid>
      </Grid>
      <TextField
        fullWidth
        type='email'
        label="Email"
        variant="outlined"
        sx={{ mb: 4 }}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type='tel'
            label="Phone"
            variant="outlined"
            sx={{ mb: 4 }}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Company Name"
            variant="outlined"
            sx={{ mb: 4 }}
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />
        </Grid>
      </Grid>
      <TextField
        fullWidth
        label="Address"
        variant="outlined"
        sx={{ mb: 4 }}
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        multiline
        rows={2}
      />
      <Grid container spacing={5}>
        <Grid item xs={12} md={12}>
          <FormControl fullWidth>
            <InputLabel id="contact_type_label">Type</InputLabel>
            <Select
              labelId="contact_type_label"
              id="contact_type"
              label="Type"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              {data?.data.map((t: string) => (
                <MenuItem value={t} key={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={12}>
          <FormControl fullWidth>
            <InputLabel id="entity_name_label">Entity name</InputLabel>
            <Select
              labelId="entity_name_label"
              id="entity_name"
              label="Entity name"
              value={entityName}
              onChange={(e) => setEntityName(e.target.value)}
            >
              {entityNames?.data.map((t: string) => (
                <MenuItem value={t} key={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="contained" onClick={handleSubmit} disabled={email === '' || !type || !entityName}>
          Save Contact
        </Button>
      </Box>
    </Box>
  );
};
